import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { Currency } from '@syfo/types/vault.types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Get the currency symbol for a given Currency enum value
 * 
 * @param currency Currency enum value
 * @returns Currency symbol string (£, $, €)
 */
export function getCurrencySymbol(currency: Currency): string {
  switch (currency) {
    case Currency.GBP:
      return '£';
    case Currency.AUD:
    case Currency.CAD:
    case Currency.NZD:
    case Currency.USD:
      return '$';
    case Currency.EUR:
      return '€';
    default:
      return '£';
  }
}

/**
 * Get the appropriate locale for a given Currency
 * 
 * @param currency Currency enum value
 * @returns Locale string for Intl.NumberFormat
 */
export function getCurrencyLocale(currency: Currency): string {
  switch (currency) {
    case Currency.GBP:
      return 'en-GB';
    case Currency.USD:
      return 'en-US';
    case Currency.EUR:
      return 'de-DE'; // Use German format for Euro (common standard)
    case Currency.AUD:
      return 'en-AU';
    case Currency.CAD:
      return 'en-CA';
    case Currency.NZD:
      return 'en-NZ';
    default:
      return 'en-GB';
  }
}

/**
 * Format a number as currency using the appropriate locale and currency code
 * 
 * @param amount Number to format
 * @param currency Currency enum value
 * @param options Optional Intl.NumberFormatOptions to override defaults
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number, 
  currency: Currency, 
  options?: Intl.NumberFormatOptions
): string {
  // Ensure currency is a valid string value
  const currencyCode = String(currency || Currency.GBP);
  
  // Validate that it's a known currency code
  const validCurrencies = ['GBP', 'USD', 'EUR', 'AUD', 'CAD', 'NZD'];
  const validCurrency = validCurrencies.includes(currencyCode) ? currencyCode : 'GBP';
  
  const locale = getCurrencyLocale(validCurrency as Currency);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: validCurrency,
    ...options
  }).format(amount);
}

/**
 * Safely parse a date value that could be a string, Date object, or other type
 * Returns null if the value cannot be parsed as a valid date
 */
export function safeParseUtcDate(dateValue: unknown): Date | null {
  if (!dateValue) return null;
  
  // Handle various input types
  let dateString: string;
  if (typeof dateValue === 'string') {
    dateString = dateValue.trim();
  } else if (dateValue instanceof Date) {
    return dateValue;
  } else {
    return null;
  }
  
  if (dateString === '') return null;
  
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Safely parse a date-only string (yyyy-MM-dd) as local midnight.
 * Unlike `new Date('yyyy-MM-dd')` which parses as UTC midnight, this returns
 * a Date at midnight in the local timezone, preventing off-by-one day errors
 * when comparing with `new Date()` across DST boundaries or timezone offsets.
 */
export function safeParseLocalDate(dateValue: unknown): Date | null {
  if (!dateValue) return null;

  let dateString: string;
  if (typeof dateValue === 'string') {
    dateString = dateValue.trim();
  } else if (dateValue instanceof Date) {
    return dateValue;
  } else {
    return null;
  }

  if (dateString === '') return null;

  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const date = new Date(parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10));
    return isNaN(date.getTime()) ? null : date;
  }

  // Fall back to standard parsing for non-date-only strings
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}
